import type { SupportedLocale } from "@app/i18n";

/** 정렬에 필요한 프로바이더의 최소 형태. 레지스트리(socialProviders.tsx)의 항목이
 * 이 형태를 만족하지만, 순수 함수로 두어 단위 테스트에서 가짜 프로바이더로도 검증할 수 있게
 * id/primaryFor 만 요구한다. */
export interface SocialProviderMeta {
  id: string;
  /** 이 언어에서 "우선(primary)"으로 노출할지. 비면 항상 "더 보기" 안으로 접힌다. */
  primaryFor: readonly SupportedLocale[];
}

export interface SortedProviders<T extends SocialProviderMeta> {
  /** 직전에 이 방법으로 로그인했다면 그 프로바이더(providers 안에 있을 때만). 없으면 null. */
  recent: T | null;
  /** 감지된 언어의 primaryFor 에 든 프로바이더(recent 제외) — 바로 노출한다. */
  primary: T[];
  /** 나머지 — "더 보기"로 접는다. */
  secondary: T[];
}

/**
 * 소셜 로그인 버튼을 "이 사용자에게 얼마나 관련 있는가" 순으로 3분한다.
 *
 *   1) recent    — lastUsed 와 일치하는 프로바이더를 맨 위에 "최근 사용" 뱃지로.
 *   2) primary   — 감지된 언어(locale)의 primaryFor 에 든 것(recent 제외).
 *   3) secondary — 그 외 전부. "더 보기" 안으로.
 *
 * 각 버킷 안의 순서는 입력 providers 순서를 그대로 보존한다(레지스트리 등록 순 = 노출 순).
 * lastUsed 가 "email" 처럼 소셜 프로바이더가 아니면 recent 는 null 이다 — 이메일 폼은
 * 어차피 항상 보이므로 별도 강조하지 않는다.
 */
export function sortSocialProviders<T extends SocialProviderMeta>(
  providers: readonly T[],
  locale: SupportedLocale,
  lastUsed: string | null,
): SortedProviders<T> {
  const recent = providers.find((p) => p.id === lastUsed) ?? null;
  const rest = providers.filter((p) => p !== recent);
  const primary = rest.filter((p) => p.primaryFor.includes(locale));
  const secondary = rest.filter((p) => !p.primaryFor.includes(locale));
  return { recent, primary, secondary };
}
